import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useApolloClient, useReactiveVar } from '@apollo/client/react'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  NativeSelect,
  OutlinedInput,
  Radio,
  RadioGroup,
} from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import * as Sentry from '@sentry/browser'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { boolean, date, object, string } from 'yup'

import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '../constants'
import { PhoneInputMask } from '../PhoneInputMask'
import { Subheader } from '../Subheader/Subheader'
import { buildBillingParty } from '../billingPartyHelpers'
import { REQUEST_APPOINTMENT } from './requestMutation'
import { parseMarkdownLinks } from '../parseMarkdownLinks'
import { useLocation } from '../useLocation'
import { getReferrer } from '../getReferrer'

import './style.css'

export const RequestForm = ({
  alertType,
  appointmentRequestId,
  locationData,
  message,
  patientData,
  responsiblePartyData,
  store,
}) => {
  const client = useApolloClient()
  const { locationId, partnerId, providerId, source, attributionData } = useReactiveVar(store)
  const location = useReactiveVar(locationData)
  const patient = useReactiveVar(patientData)
  const responsibleParty = responsiblePartyData ? useReactiveVar(responsiblePartyData) : null
  const [isBackdropOpen, setIsBackdropOpen] = useState(false)
  const [isResponsibleParty, setResponsibleParty] = useState('self')
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { insuranceRequired, commentRequired, customTermsAddendum, patientSexOptions, isPatientSexRequired } =
    useLocation(store)
  const referrer = getReferrer()
  const FIRST_AVAILABLE_VALUE = 'First available'
  const termsOfUseLabel = { inputProps: { 'aria-label': 'Terms of Use' } }
  const resolvedLocale = i18n.resolvedLanguage
    ? i18n.resolvedLanguage.toUpperCase()
    : 'EN'
  const patientSexLabelMap = {
    FEMALE: t('Female'),
    MALE: t('Male'),
    OTHER: t('Other'),
    UNKNOWN: t('Unknown'),
    PREFER_NOT_TO_SAY: t('Prefer not to say'),
  }
  const locationProviders = useMemo(() => location?.providers ?? [], [location])

  // Define min and max birthdates for dob field validation
  const maxDob = new Date()
  maxDob.setFullYear(maxDob.getFullYear() - 1)
  const minDob = new Date()
  minDob.setFullYear(minDob.getFullYear() - 120)
  const minDobFormatted = format(minDob, 'MM/dd/yyyy')
  const maxDobFormatted = format(maxDob, 'MM/dd/yyyy')
  const dobValidationMsg = t(
    'Date of birth must be between {{minDobFormatted}} and {{maxDobFormatted}}',
    {
      minDobFormatted,
      maxDobFormatted,
      interpolation: { escapeValue: false },
    }
  )

  // Define allowed characters for name field validation
  const validNameFormat = /^[\p{L}\s-]+$/u
  const nameValidationMsg = t('Name may only contain letters and hyphens')

  // Define format for email field validation
  const validEmailFormat =
    /^(?:(?:[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+(?:\.[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+)*)|(?:"(?:[\p{L}\p{N}\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\p{L}\p{N}\x01-\x09\x0b\x0c\x0e-\x7f])*"))@(?:(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+[\p{L}]{2,}|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}|IPv6:(?:[a-fA-F0-9]{1,4}(?::[a-fA-F0-9]{1,4}){7}))\])$/u
  const emailValidationMsg = t(
    'Email must be formatted as username@domain.com and cannot contain special characters in the domain'
  )

  const getValidationSchema = (isResponsibleParty, shouldRequireSex, sexOptions) =>
    object({
      firstName: string()
        .max(250)
        .matches(validNameFormat, nameValidationMsg)
        .required(t('First name required')),
      lastName: string()
        .max(250)
        .matches(validNameFormat, nameValidationMsg)
        .required(t('Last name required')),
      dob: date()
        .max(maxDob, dobValidationMsg)
        .min(minDob, dobValidationMsg)
        .required(t('Date of birth required')),
      email: string().when([], {
        is: () => isResponsibleParty === 'self',
        then: schema =>
          schema
            .max(320)
            .matches(validEmailFormat, emailValidationMsg)
            .required(t('Email required')),
        otherwise: schema => schema.notRequired(),
      }),
      phone: string().when([], {
        is: () => isResponsibleParty === 'self',
        then: schema =>
          schema
            .test(
              'is-valid-phone',
              t('Phone number should contain 10 digits'),
              value => {
                const digitsOnly = value ? value.replace(/\D/g, '') : ''
                return digitsOnly.length === 10
              }
            )
            .required(t('Mobile phone required')),
        otherwise: schema => schema.notRequired(),
      }),
      sex: string().when([], {
        is: () => shouldRequireSex,
        then: schema =>
          schema
            .required(t('Sex required'))
            .oneOf(
              sexOptions,
              t('Select a valid option from the list provided')
            ),
        otherwise: schema => schema.notRequired(),
      }),
      insuranceInfo: string()
        .max(250)
        .when([], {
          is: () => insuranceRequired && !partnerId,
          then: schema => schema.required(t('Insurance name required')),
        }),
      provider: string().optional(),
      comment: string()
        .max(500)
        .when([], {
          is: () => commentRequired,
          then: schema => schema.required(t('Comment required')),
          otherwise: schema => schema.optional(),
        }),
      termsOfUse: boolean().oneOf([true]).required(),
      responsibleParty: object({
        firstName: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(250)
              .matches(validNameFormat, nameValidationMsg)
              .required(t('First name required')),
          otherwise: schema => schema.notRequired(),
        }),
        lastName: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(250)
              .matches(validNameFormat, nameValidationMsg)
              .required(t('Last name required')),
          otherwise: schema => schema.notRequired(),
        }),
        dob: date().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(maxDob, dobValidationMsg)
              .min(minDob, dobValidationMsg)
              .required(t('Date of birth required')),
          otherwise: schema => schema.notRequired(),
        }),
        email: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .max(320)
              .matches(validEmailFormat, emailValidationMsg)
              .required(t('Email required')),
          otherwise: schema => schema.notRequired(),
        }),
        phone: string().when([], {
          is: () => isResponsibleParty === 'other',
          then: schema =>
            schema
              .test(
                'is-valid-phone',
                t('Phone number should contain 10 digits'),
                value => {
                  const digitsOnly = value ? value.replace(/\D/g, '') : ''
                  return digitsOnly.length === 10
                }
              )
              .required(t('Mobile phone required')),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    })

  const isValidProviderId = useMemo(() => {
    if (!providerId) {
      return false
    }
    const providerExists = locationProviders.some(
      provider => provider.id === providerId
    )
    if (providerExists) {
      return true
    }
    Sentry.captureMessage(
      `Supplied provider ${providerId} does not exist for location ${locationId}`
    )
    return false
  }, [locationId, locationProviders, providerId])

  const selectedProviderName = useMemo(() => {
    if (!isValidProviderId) {
      return ''
    }
    const provider = locationProviders.find(p => p.id === providerId)
    return provider?.name || ''
  }, [isValidProviderId, locationProviders, providerId])

  const formik = useFormik({
    initialValues: {
      firstName: patient?.firstName || '',
      lastName: patient?.lastName || '',
      dob: patient?.dob || '',
      email: patient?.email || '',
      phone: patient?.phone || '',
      sex: patient?.sex || '',
      insuranceInfo: patient?.insuranceInfo || '',
      provider: FIRST_AVAILABLE_VALUE,
      comment: patient?.comment || '',
      termsOfUse: false,
      responsibleParty: {
        firstName: responsibleParty?.firstName || '',
        lastName: responsibleParty?.lastName || '',
        email: responsibleParty?.email || '',
        phone: responsibleParty?.phone || '',
        dob: responsibleParty?.dob || '',
      },
    },
    validationSchema: getValidationSchema(
      isResponsibleParty,
      isPatientSexRequired,
      patientSexOptions
    ),
    onSubmit: async values => {
      setIsBackdropOpen(true)
      setIsSubmittingRequest(true)
      const { termsOfUse, provider, responsibleParty: rpValues, ...patientValues } = values

      // Get email/phone from responsible party if booking for other
      if (isResponsibleParty === 'other') {
        patientValues.email = rpValues.email
        patientValues.phone = rpValues.phone
      }

      if (partnerId && !patientValues.insuranceInfo) {
        patientValues.insuranceInfo = 'No insurance'
      }
      if (!isPatientSexRequired || !patientValues.sex) {
        delete patientValues.sex
      }
      patientData(patientValues)

      const billingPartyValues =
        isResponsibleParty === 'other'
          ? buildBillingParty(isResponsibleParty, values)
          : null

      if (billingPartyValues && responsiblePartyData) {
        responsiblePartyData(billingPartyValues)
      }

      const variables = {
        locationId,
        partnerId,
        patient: patientValues,
        locale: resolvedLocale,
        referrer: referrer,
        ...(source && { source }),
        ...(values.provider !== FIRST_AVAILABLE_VALUE && {
          providerId: values.provider,
        }),
        // Only include attributionData if it has at least one non-null, non-empty value
        ...(attributionData && Object.values(attributionData).some(value => value !== null && value !== '') && { attributionData }),
        ...(billingPartyValues && { billingParty: billingPartyValues }),
      }
      try {
        const { data } = await client.mutate({
          mutation: REQUEST_APPOINTMENT,
          variables,
        })
        const result = data?.requestAppointment

        if (result?.__typename === 'UserInputValidationFailed') {
          const fieldErrors = result.fieldErrors ?? []
          let emailErrorMessage = ''
          let phoneErrorMessage = ''
          const otherFieldMessages = []

          fieldErrors.forEach(fieldError => {
            const nestedErrors = fieldError.errors ?? []

            if (fieldError.field === 'patient') {
              nestedErrors.forEach(error => {
                if (error.field === 'email') {
                  emailErrorMessage = error.message
                } else if (error.field === 'phone') {
                  phoneErrorMessage = error.message
                } else if (error.field === 'sex') {
                  formik.setFieldTouched('sex', true, false)
                  formik.setFieldError('sex', error.message)
                } else if (error.message) {
                  otherFieldMessages.push(error.message)
                }
              })
            } else {
              nestedErrors.forEach(error => {
                if (error.message) {
                  otherFieldMessages.push(error.message)
                }
              })
            }
          })

          if (emailErrorMessage) {
            const emailFieldPath =
              isResponsibleParty === 'other'
                ? 'responsibleParty.email'
                : 'email'
            formik.setFieldTouched(emailFieldPath, true, false)
            formik.setFieldError(emailFieldPath, emailErrorMessage)
          }

          if (phoneErrorMessage) {
            const phoneFieldPath =
              isResponsibleParty === 'other'
                ? 'responsibleParty.phone'
                : 'phone'
            formik.setFieldTouched(phoneFieldPath, true, false)
            formik.setFieldError(phoneFieldPath, phoneErrorMessage)
          }

          if (otherFieldMessages.length > 0) {
            alertType('error')
            message(otherFieldMessages.join(' '))
          } else if (!emailErrorMessage && !phoneErrorMessage) {
            alertType('error')
            message(result.message || t('Please review your information and try again.'))
          }
          return
        }

        if (data.requestAppointment && data.requestAppointment.id) {
          if (appointmentRequestId) {
            appointmentRequestId(data.requestAppointment.id)
          }
          navigate('/request-confirmation')
        }
      } catch (error) {
        alertType('error')
        message(
          t(
            "Oops! Something didn't quite go as planned. Give it another try later and we'll get things sorted out."
          )
        )
        navigate('/')
      } finally {
        setIsSubmittingRequest(false)
        setIsBackdropOpen(false)
      }
    },
  })
  const { setFieldValue } = formik

  useEffect(() => {
    if (isValidProviderId) {
      setFieldValue('provider', providerId)
    }
  }, [isValidProviderId, providerId, setFieldValue])

  return (
    <form
      className="form"
      onSubmit={formik.handleSubmit}
      data-testid="request-form"
    >
      <FormControl aria-label="responsible-party-section" fullWidth>
        <FormLabel id="responsible-party" sx={{ textAlign: 'center' }}>
          {t('Is this appointment for yourself or another person?')}
        </FormLabel>
        <RadioGroup
          aria-labelledby="Responsible party"
          name="responsible-party"
          value={isResponsibleParty}
          onChange={event => {
            setResponsibleParty(event.target.value)
          }}
          row
          sx={{ justifyContent: 'center' }}
        >
          <FormControlLabel
            value="self"
            control={<Radio />}
            label={t('Self')}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label={t('Other')}
          />
        </RadioGroup>
      </FormControl>
      {isResponsibleParty === 'other' && (
        <>
          <Subheader text={t('Responsible Party')} />
          <TextField
            id="responsible-party-first-name"
            value={formik.values.responsibleParty.firstName}
            name="responsibleParty.firstName"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label={t('Responsible Party First Name')}
            variant="outlined"
            disabled={formik.isSubmitting}
            InputProps={{
              inputProps: { maxLength: 50 },
            }}
            error={
              formik.touched.responsibleParty?.firstName &&
              Boolean(formik.errors.responsibleParty?.firstName)
            }
            helperText={
              formik.touched.responsibleParty?.firstName &&
              formik.errors.responsibleParty?.firstName
            }
            required
            fullWidth
          />
          <TextField
            id="responsible-party-last-name"
            value={formik.values.responsibleParty.lastName}
            name="responsibleParty.lastName"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.responsibleParty?.lastName &&
              Boolean(formik.errors.responsibleParty?.lastName)
            }
            helperText={
              formik.touched.responsibleParty?.lastName &&
              formik.errors.responsibleParty?.lastName
            }
            disabled={formik.isSubmitting}
            label={t('Responsible Party Last Name')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="responsible-party-date-of-birth"
            data-testid="responsible-party-dob"
            value={formik.values.responsibleParty.dob}
            name="responsibleParty.dob"
            type="date"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label={t('Responsible Party Date of Birth')}
            variant="outlined"
            error={
              formik.touched.responsibleParty?.dob &&
              Boolean(formik.errors.responsibleParty?.dob)
            }
            helperText={
              formik.touched.responsibleParty?.dob &&
              formik.errors.responsibleParty?.dob
            }
            InputLabelProps={{ shrink: true }}
            disabled={formik.isSubmitting}
            required
            fullWidth
          />
          <TextField
            id="responsible-party-email"
            value={formik.values.responsibleParty.email}
            name="responsibleParty.email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.responsibleParty?.email &&
              Boolean(formik.errors.responsibleParty?.email)
            }
            helperText={
              formik.touched.responsibleParty?.email &&
              formik.errors.responsibleParty?.email
            }
            disabled={formik.isSubmitting}
            label={t('Responsible Party Email')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="responsible-party-phone"
            type="tel"
            name="responsibleParty.phone"
            variant="outlined"
            label={t('Responsible Party Mobile Phone')}
            value={formik.values.responsibleParty.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.responsibleParty?.phone &&
              Boolean(formik.errors.responsibleParty?.phone)
            }
            helperText={
              formik.touched.responsibleParty?.phone &&
              formik.errors.responsibleParty?.phone
            }
            disabled={formik.isSubmitting}
            InputProps={{ inputComponent: PhoneInputMask }}
            required
            fullWidth
          />
        </>
      )}
      <Subheader text={t('Patient Information')} />
      <TextField
        id="first-name"
        value={formik.values.firstName}
        name="firstName"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        label={t('Patient First Name')}
        variant="outlined"
        disabled={formik.isSubmitting}
        InputProps={{
          inputProps: { maxLength: 50 },
        }}
        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
        helperText={formik.touched.firstName && formik.errors.firstName}
        required
        fullWidth
      />
      <TextField
        id="last-name"
        value={formik.values.lastName}
        name="lastName"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
        helperText={formik.touched.lastName && formik.errors.lastName}
        disabled={formik.isSubmitting}
        label={t('Patient Last Name')}
        variant="outlined"
        required
        fullWidth
      />
      <TextField
        id="date"
        data-testid="dob"
        value={formik.values.dob}
        name="dob"
        type="date"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        label={t('Patient Date of Birth')}
        variant="outlined"
        error={formik.touched.dob && Boolean(formik.errors.dob)}
        helperText={formik.touched.dob && formik.errors.dob}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 8 }}
        disabled={formik.isSubmitting}
        required
        fullWidth
      />
      {isPatientSexRequired && (
        <TextField
          select
          id="sex"
          data-testid="patient-sex"
          value={formik.values.sex}
          name="sex"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label={t('Sex')}
          variant="outlined"
          error={
            (formik.touched.sex || formik.submitCount > 0) &&
            Boolean(formik.errors.sex)
          }
          helperText={
            (formik.touched.sex || formik.submitCount > 0) && formik.errors.sex
          }
          disabled={formik.isSubmitting}
          required
          fullWidth
        >
          <MenuItem value="">{t('Select...')}</MenuItem>
          {patientSexOptions.map(option => (
            <MenuItem key={option} value={option}>
              {patientSexLabelMap[option] ?? option}
            </MenuItem>
          ))}
        </TextField>
      )}
      {isResponsibleParty === 'self' && (
        <>
          <TextField
            id="email"
            value={formik.values.email}
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            disabled={formik.isSubmitting}
            label={t('Patient Email')}
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="phone"
            type="tel"
            name="phone"
            variant="outlined"
            label={t('Patient Mobile Phone')}
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            disabled={formik.isSubmitting}
            InputProps={{ inputComponent: PhoneInputMask }}
            required
            fullWidth
          />
        </>
      )}
      {isValidProviderId ? (
        <TextField
          id="provider"
          value={selectedProviderName}
          label={t('Provider')}
          variant="outlined"
          disabled
          fullWidth
        />
      ) : (
        <FormControl fullWidth variant="outlined">
          <InputLabel variant="outlined" htmlFor="provider">
            {t('Provider')}
          </InputLabel>
          <NativeSelect
            id="provider"
            name="provider"
            data-testid="provider-field"
            value={formik.values.provider}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
            input={<OutlinedInput label={t('Provider')} />}
            variant="outlined"
            fullWidth
          >
            <option value={FIRST_AVAILABLE_VALUE}>
              {t(FIRST_AVAILABLE_VALUE)}
            </option>
            {locationProviders.map((provider, index) => (
              <option key={index} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
      )}
      {!partnerId && insuranceRequired && (
        <TextField
          id="insurance-info"
          name="insuranceInfo"
          variant="outlined"
          label={t('Insurance Name')}
          value={formik.values.insuranceInfo}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.insuranceInfo && Boolean(formik.errors.insuranceInfo)
          }
          helperText={
            formik.errors.insuranceInfo
              ? formik.errors.insuranceInfo
              : t(`If you don't have insurance, type 'No insurance.'`)
          }
          disabled={formik.isSubmitting}
          required={insuranceRequired}
          fullWidth
        />
      )}
      <TextField
        id="comment"
        value={formik.values.comment}
        name="comment"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        label={t('Comment')}
        variant="outlined"
        minRows={2}
        maxRows={4}
        fullWidth
        multiline
        error={formik.touched.comment && Boolean(formik.errors.comment)}
        disabled={formik.isSubmitting}
        required={commentRequired}
        helperText={
          formik.errors.comment
            ? formik.errors.comment
            : t(`Do not include any protected health information in the comment
        field. Additional details can be provided when contacted by the practice
        directly.`)
        }
        InputProps={{
          inputProps: { maxLength: 500 },
          sx: [
            {
              textarea: { marginBottom: theme => theme.spacing(2) },
            },
          ],
          endAdornment: (
            <InputAdornment
              disableTypography
              disablePointerEvents
              position="start"
              sx={[{ alignSelf: 'flex-end', position: 'absolute' }]}
            >
              {formik.values.comment.length} {t('characters')}
            </InputAdornment>
          ),
        }}
      />
      <Grid
        container
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1.5}
      >
        <Grid
          container
          item
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          wrap="nowrap"
          gap={1}
        >
          <Checkbox
            {...termsOfUseLabel}
            name="termsOfUse"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            color="primary"
            required
            sx={{ pt: 0.25 }}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="caption" color="rgba(0, 0, 0, 0.6)">
              I have read and accept Intelibly's{' '}
              <Link
                data-testid="form-terms-and-conditions"
                variant="caption"
                href={TERMS_AND_CONDITIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </Link>{' '}
              and I consent to Intelibly collecting data, including sensitive
              information as fully described in the{' '}
              <Link
                data-testid="form-privacy-policy"
                variant="caption"
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </Typography>
            {customTermsAddendum && (
              <Typography
                variant="caption"
                color="rgba(0, 0, 0, 0.6)"
                component="div"
                data-testid="custom-terms-addendum"
              >
                {parseMarkdownLinks(customTermsAddendum)}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item>
          <Button
            aria-label="confirm"
            color="primary"
            disabled={formik.isSubmitting}
            disableElevation
            fullWidth
            type="submit"
            variant="contained"
          >
            {t('Confirm')}
          </Button>
        </Grid>
      </Grid>
      <Backdrop
        open={isBackdropOpen || isSubmittingRequest}
        sx={{
          color: '#fff',
          opacity: 2,
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </form>
  )
}

RequestForm.propTypes = {
  alertType: PropTypes.func.isRequired,
  appointmentRequestId: PropTypes.func,
  locationData: PropTypes.func.isRequired,
  message: PropTypes.func.isRequired,
  patientData: PropTypes.func.isRequired,
  responsiblePartyData: PropTypes.func,
  store: PropTypes.func.isRequired,
}
